import { BaseApi } from '@/main/baseApi';
import { PARTNER } from '@/modules/kartoteka/constants';

import {
	CUSTOMER_CONTACT_DETAILS, CDB, DependentTableNames, DMS_MODULE, DUPLICITY_CHECK_API, DUPLICITY_TABLE, MERGE, NAME, VOZIDLO_MERGE_VALUES,
	CUSTOMER_FILES, VOZIDLO_ONGOING_EDITS, VEHICLE_HISTORY, VEHICLE_INSURANCE_DETAILS, CUSTOMER_OFFERS, INSURANCE_REPORT
} from './constants';

class VozidloProvider extends BaseApi {
	schemaUrl(tableName: DependentTableNames) {
		switch (tableName) {
			case CUSTOMER_CONTACT_DETAILS:
				return `/${this.area}/${NAME}/CustomerContactDetailsSchema`;
			case MERGE:
				return `/${this.area}/${NAME}/MergeValuesSchema`;
			case DUPLICITY_TABLE:
				return `/${this.area}/${NAME}/${DUPLICITY_CHECK_API}Schema`;
			case VOZIDLO_MERGE_VALUES:
				return `/${this.area}/${NAME}/${VOZIDLO_MERGE_VALUES}Schema`;
			case CUSTOMER_FILES:
				return `/${this.area}/${NAME}/CustomerFilesSchema`;
			case VOZIDLO_ONGOING_EDITS:
				return `/${this.area}/${NAME}/${VOZIDLO_ONGOING_EDITS}Schema`;
			case VEHICLE_HISTORY:
				return `/${this.area}/${NAME}/${VEHICLE_HISTORY}Schema`;
			case VEHICLE_INSURANCE_DETAILS:
				return `/${this.area}/${NAME}/${VEHICLE_INSURANCE_DETAILS}Schema`;
			case CUSTOMER_OFFERS:
				return `/${this.area}/${NAME}/${CUSTOMER_OFFERS}Schema`;
			case INSURANCE_REPORT:
				return `/${this.area}/${NAME}/InsuranceEvaluationReportSchema`;
			default:
				throw new Error(`schema for ${tableName} is not implemented yet`);
		}
	}

	tableUrl(tableName: DependentTableNames) {
		switch (tableName) {
			case CUSTOMER_CONTACT_DETAILS:
				return `/${this.area}/${NAME}/CustomerContactDetailsRows`;
			case DUPLICITY_TABLE:
				return `/${this.area}/${NAME}/${DUPLICITY_CHECK_API}Rows`;
			case MERGE:
				return `/${this.area}/${NAME}/MergeValuesRows`;
			case VOZIDLO_MERGE_VALUES:
				return `/${this.area}/${NAME}/${VOZIDLO_MERGE_VALUES}Rows`;
			case CUSTOMER_FILES:
				return `/${this.area}/${NAME}/CustomerFilesRows`;
			case VOZIDLO_ONGOING_EDITS:
				return `/${this.area}/${NAME}/${VOZIDLO_ONGOING_EDITS}Rows`;
			case VEHICLE_HISTORY:
				return `/${this.area}/${NAME}/${VEHICLE_HISTORY}Rows`;
			case VEHICLE_INSURANCE_DETAILS:
				return `/${this.area}/${NAME}/${VEHICLE_INSURANCE_DETAILS}Rows`;
			case CUSTOMER_OFFERS:
				return `/${this.area}/${NAME}/${CUSTOMER_OFFERS}Rows`;
			case INSURANCE_REPORT:
				return `/${this.area}/${NAME}/InsuranceEvaluationReportRows`;
			default:
				throw new Error(`data for ${tableName} is not implemented yet`);
		}
	}

	customerFilesRows(filter: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/CustomerFilesRows`, filter);
	}

	insuranceEvaluationReportRows(filter: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/insuranceEvaluationReportRows`, filter);
	}

	saveDocument(fileList: any, ZakaznikId: null | number) {
		const file = new FormData();
		file.append('file', fileList.raw, fileList.name);
		return this.uploadMultipart(`/${DMS_MODULE}/${PARTNER}/SaveDocument?ZakaznikId=${ZakaznikId}`, file);
	}

	getDocuments(fileId: number) {
		return this.fetch(`/${DMS_MODULE}/${PARTNER}/GeCustomerFilestDocument?fileId=${fileId}`);
	}

	deleteDocuments(fileId: number) {
		return this.post(`/${DMS_MODULE}/${PARTNER}/DeleteDocument?fileId=${fileId}`);
	}

	histOpravSchema() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/HistOpravSchema`);
	}

	histOpravRows(filter: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/HistOpravRows`, filter);
	}

	customerFeedbackSchema() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/CustomerFeedbackSchema`);
	}

	obratyOpravSchema() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ObratyOpravSchema`);
	}

	obratyOpravRows(filter: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ObratyOpravRows`, filter);
	}

	serviceKeySchema() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ServiceKeySchema`);
	}

	serviceKeyRows(filter: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/ServiceKeyRows`, filter);
	}

	sdaSchema() {
		return this.fetch(`/${DMS_MODULE}/${NAME}/SdaSchema`);
	}

	sdaRows(filter: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/SdaRows`, filter);
	}

	getVozidlo(vozidloId: number | null) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/${vozidloId}`);
	}

	async vozidloGetCdbDmz(vozidlo: number | string) {
		return this.post(`/${DMS_MODULE}/${NAME}/${CDB}/GetCdbDzmStatus`, vozidlo);
	}

	async vozidloCdbPrepaidService(vozidlo: number | string) {
		return this.post(`/${DMS_MODULE}/${NAME}/${CDB}/GetCdbPrepaidServiceBc`, vozidlo);
	}

	zmenaMajitele(filter: any) {
		return this.post(`/${DMS_MODULE}/${NAME}/ZmenMajitele`, filter);
	}

	typKodChange(filter: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/TypKodChange`, filter);
	}

	spzExit(filter: any) {
		return this.post(`/${DMS_MODULE}/${NAME}/SpzExit`, filter);
	}

	karoserieNrExit(filter: any) {
		return this.post(`/${DMS_MODULE}/${NAME}/KaroserieNrExit`, filter);
	}

	vinExit(filter: any) {
		return this.post(`/${DMS_MODULE}/${NAME}/KaroserieNrExit`, filter);
	}

	insertDueDate(vozidloDTO: any) {
		return this.post(`/${DMS_MODULE}/${NAME}/InsertDueDate`, vozidloDTO);
	}

	updateVozidloOngoingEditData(updateData: any) {
		this.update(`/${DMS_MODULE}/${NAME}/UpdateVozidloOngoingEditData`, updateData);
	}

	async sendOTP(props: object): Promise<Record<string, any>> {
		return this.post(`/${DMS_MODULE}/${NAME}/SendOTP`, props);
	}

	rejectDetails(params: object) {
		return this.update(`/${DMS_MODULE}/${NAME}/RejectDetails`, params);
	}

	async verifyOTP(props: object): Promise<Record<string, any>> {
		return this.post(`/${DMS_MODULE}/${NAME}/VerifyOTP`, props);
	}

	approveDetails(params: object) {
		return this.update(`/${DMS_MODULE}/${NAME}/ApproveDetails`, params);
	}

	getCustomerFeedBackData(OrderNo: any) {
		return this.fetch(`CustomerFeedback/GetCustomerFeedback?OrderNo=${OrderNo}`);
	}

	vechicleCustomerAvgFeedbackRows(filter: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/VechicleCustomerAvgFeedbackRows`, filter);
	}

	async getDetailsForInsurancePolicy(dto: any) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/GetDetailsForInsurancePolicy`, dto);
	}

	async getInsuranceDetailsForUpdate(id:number) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/GetInsuranceDetailsForUpdate?id=${id}`);
	}

	async insertInsurancePolicy(dto:any) {
		const insertInsurancePolicyResult = await this.post(`/${DMS_MODULE}/${NAME}/InsertInsurancePolicy`, dto);
		return insertInsurancePolicyResult;
	}

	async updateInsurancePolicy(dto: any) {
		return this.post(`/${DMS_MODULE}/${NAME}/UpdateInsurancePolicy`, dto);
	}

	async isValidPolicy(vehicleNumber: number, policyStartDate: string, id: number) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/IsValidPolicy?VIN=${vehicleNumber}&PolicyStartDate=${policyStartDate}&ID=${id}`);
	}

	async savePolicyDocument(fileList: any, id: null | number) {
		const file = new FormData();
		file.append('file', fileList.raw, fileList.name);
		return this.uploadMultipart(`/${DMS_MODULE}/${NAME}/SaveDocument?ID=${id}`, file);
	}

	submitUploadForInsuranceEvaluation(fileListEV: any, VozidloId: null | number) {
		const file = new FormData();
		file.append('file', fileListEV.raw, fileListEV.name);
		return this.uploadMultipart(`/${DMS_MODULE}/${PARTNER}/SaveInsuranceEvaluationDocument?VozidloId=${VozidloId}`, file);
	}

	getInsuranceEvaluationDocuments(fileId: number) {
		return this.fetch(`/${DMS_MODULE}/${PARTNER}/GetInsuranceEvaluationDocument?fileId=${fileId}`);
	}

	getVehicleDetailsByVIN(vin: string) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/GetVehicleDetailsByVIN?vin=${vin}`);
	}

	getVehicleCustomerDetailsByVIN(vin: string | null) {
		return this.fetch(`/${DMS_MODULE}/${NAME}/GetVehicleAndCustomerDetailsByVIN?vin=${vin}`);
	}
}

export const apiProvider = new VozidloProvider(DMS_MODULE, NAME);
